import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { imageSrc } from "../../lib/api";
import { nok, productUrl, stockMeta } from "../../lib/shop";
import { useLang } from "../../context/LanguageContext";

// Nøytral produkt-plassholdar (design_guidelines → butikk_module_addition_2026_10.image_urls.product_placeholder)
export const PRODUCT_PLACEHOLDER = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=1200&q=80";

export const ProductImage = ({ product, className = "", sizes }) => {
  const src = product?.image_url ? imageSrc(product.image_url) : PRODUCT_PLACEHOLDER;
  return (
    <img
      src={src}
      alt={product?.name || ""}
      className={`h-full w-full object-cover ${className}`}
      loading="lazy"
      sizes={sizes}
      onError={(e) => {
        e.currentTarget.src = PRODUCT_PLACEHOLDER;
      }}
      data-testid={`product-card-image-${product?.id || "preview"}`}
    />
  );
};

export const ProductCard = ({ product, showOrganizer = true, testIdPrefix = "product-card" }) => {
  const { t } = useLang();
  const stock = stockMeta(product);
  const soldOut = product.stock_status === "sold_out";
  return (
    <Link to={productUrl(product.organizer_id, product.id)} className="block group h-full" data-testid={`${testIdPrefix}-${product.id}`}>
      <Card className="overflow-hidden card-lift h-full border-border bg-card flex flex-col" data-testid={`product-card-${product.id}-card`}>
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <ProductImage product={product} className={soldOut ? "opacity-70" : ""} />
          {stock && (
            <div className="absolute top-3 right-3">
              <Badge className={`shadow-sm ${stock.className}`} data-testid={`product-card-stock-badge-${product.id}`}>{stock.label}</Badge>
            </div>
          )}
        </div>
        <CardContent className="p-4 flex flex-col gap-2 flex-1">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-semibold leading-snug line-clamp-2" data-testid={`product-card-title-${product.id}`}>{product.name}</h3>
            {showOrganizer && product.organizer_name && (
              <p className="text-xs text-muted-foreground mt-1 truncate" data-testid={`product-card-organizer-${product.id}`}>{product.organizer_name}</p>
            )}
          </div>
          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="font-heading font-semibold tabular-nums" data-testid={`product-card-price-${product.id}`}>{product.price === 0 ? t("Gratis") : nok(product.price)}</span>
            <Button asChild={false} variant="secondary" size="sm" className="pointer-events-none" tabIndex={-1} data-testid={`product-card-view-button-${product.id}`}>
              <ShoppingBag className="w-3.5 h-3.5 mr-1.5" /> {t("Se vare")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export const ProductGrid = ({ products, showOrganizer = true, testId = "product-grid" }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6" data-testid={testId}>
    {products.map((p) => (
      <ProductCard key={p.id} product={p} showOrganizer={showOrganizer} />
    ))}
  </div>
);
